import { type RefObject, useEffectEvent, useLayoutEffect } from 'react';

interface UseIntersectionObserverParams {
  root?: RefObject<Element | null>;
  target: RefObject<Element | null>;
  onIntersect?: () => void;
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
}

export function useIntersectionObserver({
  root,
  target,
  onIntersect,
  threshold = 0.001,
  rootMargin = '0px',
  enabled = true,
}: UseIntersectionObserverParams) {
  const handleIntersect = useEffectEvent(() => {
    onIntersect?.();
  });

  useLayoutEffect(() => {
    const element = target.current;

    if (!element || !enabled || !onIntersect) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleIntersect();
          }
        });
      },
      {
        root: root?.current ?? null,
        rootMargin,
        threshold,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [enabled, onIntersect, root, rootMargin, target, threshold]);
}
